/**
 * Helpers for rendering rate-level policies (cancellation, deposit, guarantee) on the hotel checkout.
 *
 * BINDING text comes from the structured GDS terms (`summarizeCancelPolicy` / `summarizeDepositPolicy`)
 * — these are the deterministic, legally-defensible facts (deadline + penalty). The room-enrichment
 * agent's natural-language prose (`rate.generated.*`) is a *friendly summary*, surfaced separately via
 * `cancellationSummary()`, never as the binding line — an LLM can soften or omit a penalty, which is
 * exactly the misrepresentation risk we must avoid (ELARA-829 R1). Returns `undefined` when no data is
 * available so callers can hide the row entirely.
 */

import type { RoomRateModel } from '#models/RoomRateModel.js';

import { describeCancellationPolicyFacts, summarizeCancelPolicy, summarizeDepositPolicy } from '#lib/room-utils.js';

export function describeCancellationPolicy(rate: RoomRateModel | undefined, checkInDate?: string): string | undefined {
  return describeCancellationPolicyFacts(
    {
      cancellationPolicies: rate?.cancellationPolicies,
      generatedCancellationPolicy: rate?.generated?.cancellationPolicy,
      refundable: rate?.refundable,
    },
    checkInDate,
  );
}

/**
 * One line per cancellation tier, for a policy the hotel stated (Travelport Rules, `source: 'rules'`)
 * or any policy with more than one tier. A single search-estimated tier gets no list: the headline
 * already says what it can, and a list of one estimate would read as more certainty than we have.
 */
export function cancellationTierLines(rate: RoomRateModel | undefined, checkInDate?: string): string[] {
  const policies = rate?.cancellationPolicies ?? [];
  const stated = policies.some((p) => p.source === 'rules');
  if (!stated && policies.length < 2) return [];
  return summarizeCancelPolicy(policies, checkInDate);
}

/** The room-enrichment agent's plain-language cancellation summary, shown beneath the binding terms. */
export function cancellationSummary(rate: RoomRateModel | undefined): string | undefined {
  return rate?.generated?.cancellationPolicy?.trim() || undefined;
}

/**
 * The hotel's VERBATIM cancellation rule from Sabre's `additionalDetails` (the "Cancellation information"
 * entry). The property's exact words — which carry a precise cutoff TIME when the hotel states one (e.g.
 * "Cancel by 3:00 pm one day prior to arrival") that the structured offset and the LLM paraphrase both
 * drop. Preferred over the paraphrase for binding display (ELARA-829 R1).
 */
export function cancellationVerbatim(rate: RoomRateModel | undefined): string | undefined {
  const detail = rate?.additionalDetails?.find((d) => /cancel/i.test(d.description ?? ''));
  const text = detail?.text
    ?.map((line) => line.trim())
    .filter(Boolean)
    .join(' ');
  return text || undefined;
}

export function describeDepositPolicy(rate: RoomRateModel | undefined): string | undefined {
  const summarized = summarizeDepositPolicy(rate?.depositPolicies);
  if (summarized.length > 0) {
    // A rate loaded as prepaid in full whose deposit is marked "a portion of the total" is the
    // supplier contradicting itself (Barrocal, 2026-09-29: PrepayRequired, a one-night deposit
    // with the balance later, and the hotel's own words saying 50%). Shown, not resolved: what the
    // card is charged today is the hotel's decision, and the client should hear that before paying.
    const contradiction =
      rate?.prePaymentRequired && rate.depositPolicies?.some((p) => p.partial)
        ? ' The rate is loaded as prepaid in full, while the hotel’s system lists this deposit with the balance later — what your card is charged today is set by the hotel.'
        : '';
    return summarized.join(' · ') + contradiction;
  }
  const generated = rate?.generated?.depositPolicy?.trim();
  if (generated) return generated;
  if (rate?.depositRequired) return 'Deposit required at booking — see rate terms.';
  if (rate?.prePaymentRequired) return 'Pre-payment required.';
  return undefined;
}

/**
 * Most rates that don't carry a structured `depositPolicies` block instead
 * surface a credit-card-guarantee requirement (e.g. Sabre's `Guarantee`
 * response with `GuaranteesAccepted`). The room guide shows it as a
 * separate line; surfacing it here mirrors that behaviour so the checkout
 * summary doesn't appear to lose policy context vs the rate picker.
 */
export function describeGuaranteePolicy(rate: RoomRateModel | undefined): string | undefined {
  // The hotel — not Elara — is the merchant of record that charges the card. Carried on the guarantee
  // line because a card is required on every booking, so this section (and the disclosure) always renders.
  const merchant = 'Any charge to your card comes from the hotel, not Elara.';
  const cards = rate?.guaranteePolicy?.guaranteesAccepted ?? [];
  if (rate?.guaranteeRequired || cards.length > 0) {
    return `A credit card is required to guarantee the booking. ${merchant}`;
  }
  const generated = rate?.generated?.guaranteePolicy?.trim();
  if (generated) return `${generated} ${merchant}`;
  return `A credit card is required to guarantee the booking. ${merchant}`;
}
